/**
 * Edit a team's email and manager.
 *
 * The name is not editable: tickets route on it, so it stays an Ivanti console
 * change. The manager comes from the directory search (the same picker the
 * request forms and the Migrate area use), and is sent as an Employee RecId —
 * the server fills in the name and email Ivanti keeps on the team.
 *
 * A manager handing their own team to someone else is allowed, but unless they
 * are an admin it ends their access to it, so that is confirmed first (2 Oct).
 */

import { AlertDialog, Button, Dialog, Field, Input } from "@norican/react";
import { type FormEvent, useState } from "react";

import { ErrorState } from "@/components/common";
import { EmployeePicker } from "@/features/tickets/components/employee-picker";
import { useT } from "@/i18n";
import type { DirectoryEntry, TeamDetailResponse, TeamUpdateRequest } from "@/lib/api";

import { useUpdateTeam } from "./api";
import { needsHandoverConfirm, normaliseTeamEmail } from "./lib/team-problems";

export interface TeamDetailsDialogProps {
  team: TeamDetailResponse;
  isAdmin: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called after a change that ended the caller's access to this team. */
  onHandedOver: () => void;
}

interface TeamDetailsFormProps {
  team: TeamDetailResponse;
  isAdmin: boolean;
  onDone: (team: TeamDetailResponse) => void;
}

function TeamDetailsForm({ team, isAdmin, onDone }: TeamDetailsFormProps) {
  const { t } = useT();
  const update = useUpdateTeam(team.id);

  const [email, setEmail] = useState(team.email ?? "");
  const [managerId, setManagerId] = useState<string | null>(team.manager_id ?? null);
  const [managerName, setManagerName] = useState<string | null>(team.manager_name ?? null);
  const [confirming, setConfirming] = useState(false);

  const normalisedEmail = normaliseTeamEmail(email);
  const emailInvalid = normalisedEmail === undefined;

  function changes(): TeamUpdateRequest {
    const body: TeamUpdateRequest = {};
    if (normalisedEmail !== undefined && normalisedEmail !== (team.email ?? null)) {
      body.team_email = normalisedEmail;
    }
    if (managerId !== null && managerId !== team.manager_id) {
      body.manager_id = managerId;
    }
    return body;
  }

  function send() {
    const body = changes();
    if (Object.keys(body).length === 0) {
      onDone(team);
      return;
    }
    update.mutate(body, { onSuccess: onDone });
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (emailInvalid || update.isPending) {
      return;
    }
    if (
      needsHandoverConfirm({
        isAdmin,
        youManage: team.you_manage === true,
        currentManagerId: team.manager_id,
        newManagerId: managerId,
      })
    ) {
      setConfirming(true);
      return;
    }
    send();
  }

  return (
    <form onSubmit={handleSubmit} className="contents">
      <Dialog.Body className="flex flex-col gap-4">
        {update.error == null ? null : <ErrorState error={update.error} variant="inline" />}

        <Field label={t("teams.detail.name")} hint={t("teams.detail.nameHint")} disabled>
          <Input value={team.name} disabled readOnly />
        </Field>

        <Field
          label={t("teams.detail.email")}
          {...(emailInvalid ? { error: t("teams.detail.emailInvalid") } : {})}
        >
          <Input
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            maxLength={254}
            autoComplete="off"
          />
        </Field>

        <Field label={t("teams.detail.manager")} hint={t("teams.detail.managerHint")}>
          <EmployeePicker
            value={managerId}
            defaultDisplayValue={managerName}
            required
            aria-label={t("teams.detail.manager")}
            onChange={(employee: DirectoryEntry | null) => {
              setManagerId(employee?.id ?? null);
              setManagerName(employee?.display_name ?? null);
            }}
          />
        </Field>
      </Dialog.Body>

      <Dialog.Footer>
        <Dialog.Close render={<Button variant="outline" tone="neutral" type="button" />}>
          {t("teams.detail.cancel")}
        </Dialog.Close>
        <Button type="submit" loading={update.isPending} disabled={emailInvalid}>
          {t("teams.detail.save")}
        </Button>
      </Dialog.Footer>

      <AlertDialog.Root open={confirming} onOpenChange={setConfirming}>
        <AlertDialog.Portal>
          <AlertDialog.Backdrop />
          <AlertDialog.Popup className="flex flex-col gap-4">
            <AlertDialog.Title>{t("teams.detail.handoverTitle")}</AlertDialog.Title>
            <AlertDialog.Description>
              {t("teams.detail.handoverBody", { name: managerName ?? "", team: team.name })}
            </AlertDialog.Description>
            <div className="flex flex-wrap justify-end gap-2">
              <AlertDialog.Close render={<Button variant="outline" tone="neutral" />}>
                {t("teams.detail.cancel")}
              </AlertDialog.Close>
              <Button
                tone="danger"
                loading={update.isPending}
                onClick={() => {
                  setConfirming(false);
                  send();
                }}
              >
                {t("teams.detail.handoverConfirm")}
              </Button>
            </div>
          </AlertDialog.Popup>
        </AlertDialog.Portal>
      </AlertDialog.Root>
    </form>
  );
}

export function TeamDetailsDialog({
  team,
  isAdmin,
  open,
  onOpenChange,
  onHandedOver,
}: TeamDetailsDialogProps) {
  const { t } = useT();

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Backdrop />
        <Dialog.Popup size="md" className="flex max-h-[85vh] w-full flex-col">
          <Dialog.Header>
            <Dialog.Title>{t("teams.detail.editTitle")}</Dialog.Title>
            <Dialog.CloseButton label={t("teams.detail.cancel")} />
          </Dialog.Header>
          {/* Keyed on the team's state so reopening after a change re-seeds the
              fields from what Ivanti now holds. */}
          <TeamDetailsForm
            key={`${team.id}-${team.email ?? ""}-${team.manager_id ?? ""}-${String(open)}`}
            team={team}
            isAdmin={isAdmin}
            onDone={(updated) => {
              onOpenChange(false);
              if (!isAdmin && updated.you_manage !== true) {
                onHandedOver();
              }
            }}
          />
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
